import { createContext, useContext, useState, useEffect } from "react";
import {
  LayoutDashboard,
  Users,
  Box,
  Calendar,
  CalendarDays,
  Flame,
  ClipboardCheck,
  Truck,
  BarChart3,
  Package,
  FileSpreadsheet,
  Database,
  Receipt,
  BookOpen,
  Wallet,
  DollarSign,
  Settings,
  Building2,
  ShoppingCart,
  Warehouse,
  Navigation,
  MinusCircle,
} from "lucide-react";

// All available pages for quick access
export const AVAILABLE_PAGES = [
  { id: "dashboard", name: "Dashboard", href: "/", icon: LayoutDashboard, permission: "view_dashboard" },
  { id: "customers", name: "Customers", href: "/customers", icon: Users, permission: "view_customers" },
  { id: "castings", name: "Castings", href: "/castings", icon: Box, permission: "view_castings" },
  { id: "monthly-plan", name: "Monthly Plan", href: "/monthly-planning", icon: Calendar, permission: "view_monthly_plan" },
  { id: "daily-plan", name: "Daily Plan", href: "/daily-planning", icon: CalendarDays, permission: "view_daily_plan" },
  { id: "heats", name: "Heat Entry", href: "/heats", icon: Flame, permission: "view_heats" },
  { id: "quality", name: "Quality", href: "/quality", icon: ClipboardCheck, permission: "view_quality" },
  { id: "dispatch", name: "Dispatch", href: "/dispatch", icon: Truck, permission: "view_dispatch" },
  { id: "status", name: "Status", href: "/status", icon: Navigation, permission: "view_status" },
  { id: "sales", name: "Sales", href: "/sales", icon: ClipboardCheck, permission: "view_sales" },
  { id: "consumption", name: "Consumption", href: "/store/consumption", icon: MinusCircle, anyPermission: ["view_store", "view_store_consumption"] },
  { id: "stock", name: "Stock", href: "/stock", icon: Warehouse, permission: "view_stock" },
  { id: "vendors", name: "Vendors", href: "/purchase/vendors", icon: Building2, permission: "view_vendors" },
  { id: "purchase-orders", name: "Purchase Orders", href: "/purchase/orders", icon: FileSpreadsheet, permission: "view_purchase_orders" },
  { id: "purchase-invoices", name: "Purchase Invoices", href: "/purchase/invoices", icon: Receipt, permission: "view_purchase_invoices" },
  { id: "reports", name: "Reports", href: "/reports", icon: BarChart3, permission: "view_reports" },
  { id: "settings", name: "Settings", href: "/settings", icon: Settings },
];

// Default quick access pages
const DEFAULT_QUICK_ACCESS = ["dashboard", "sales", "dispatch", "stock", "reports"];

const QuickAccessContext = createContext(null);

export const QuickAccessProvider = ({ children }) => {
  const [quickAccessIds, setQuickAccessIds] = useState(() => {
    const saved = localStorage.getItem("foundry-quick-access");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length === 5) {
          return parsed;
        }
      } catch (e) {
        console.error("Error parsing quick access from localStorage:", e);
      }
    }
    return DEFAULT_QUICK_ACCESS;
  });

  // Save to localStorage whenever quickAccessIds changes
  useEffect(() => {
    localStorage.setItem("foundry-quick-access", JSON.stringify(quickAccessIds));
  }, [quickAccessIds]);

  // Get full page objects for current quick access
  const quickAccessPages = quickAccessIds
    .map(id => AVAILABLE_PAGES.find(p => p.id === id))
    .filter(Boolean);

  // Update quick access pages
  const setQuickAccess = (newIds) => {
    if (Array.isArray(newIds) && newIds.length === 5) {
      setQuickAccessIds(newIds);
    }
  };

  // Toggle a page in quick access (add/remove)
  const toggleQuickAccess = (pageId) => {
    if (quickAccessIds.includes(pageId)) {
      // Can't remove if only 5 left - need to replace
      return false;
    } else if (quickAccessIds.length < 5) {
      setQuickAccessIds([...quickAccessIds, pageId]);
      return true;
    }
    return false;
  };

  // Replace a page at specific index
  const replaceQuickAccess = (index, newPageId) => {
    if (index >= 0 && index < 5 && !quickAccessIds.includes(newPageId)) {
      const newIds = [...quickAccessIds];
      newIds[index] = newPageId;
      setQuickAccessIds(newIds);
      return true;
    }
    return false;
  };

  // Reset to defaults
  const resetToDefaults = () => {
    setQuickAccessIds(DEFAULT_QUICK_ACCESS);
  };

  return (
    <QuickAccessContext.Provider
      value={{
        quickAccessIds,
        quickAccessPages,
        setQuickAccess,
        toggleQuickAccess,
        replaceQuickAccess,
        resetToDefaults,
        availablePages: AVAILABLE_PAGES,
      }}
    >
      {children}
    </QuickAccessContext.Provider>
  );
};

export const useQuickAccess = () => {
  const context = useContext(QuickAccessContext);
  if (!context) {
    throw new Error("useQuickAccess must be used within a QuickAccessProvider");
  }
  return context;
};
